import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRoute } from '@react-navigation/native';
import { useScreenLoad } from '../../shared/ui/hooks-screen/useScreenLoad';
import type { ScreenRefreshResult } from '../../shared/ui/screenRefreshFeedback';
import { getSessionOrganizationId } from '../../shell/session/sessionOrganization';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { fetchOpenOrders } from '../infra/ordersRepo';
import type { CustomerOrderRow, OrderStatus } from '../domain/orderTypes';

/**
 * Cola viva (PedidosYa-style):
 * - Pendientes (default): accept/reject
 * - En preparación: confirmed + preparing
 * - Listos: ready
 * - En camino: dispatched + delivered (aún falta cerrar venta / picked_up)
 *
 * Historial cerrado vive en OrdersHistoryScreen.
 * Scope: sucursal del header.
 */
export type OrderFilterKey = 'pending' | 'active' | 'ready' | 'dispatched';

export const ORDER_FILTER_LABELS: Record<OrderFilterKey, string> = {
  pending: 'Pendientes',
  active: 'En preparación',
  ready: 'Listos',
  dispatched: 'En camino',
};

/** Display order of chips (left → right). */
export const ORDER_FILTER_KEYS: OrderFilterKey[] = [
  'pending',
  'active',
  'ready',
  'dispatched',
];

const FILTER_STATUSES: Record<OrderFilterKey, OrderStatus[]> = {
  pending: ['pending'],
  active: ['confirmed', 'preparing'],
  ready: ['ready'],
  dispatched: ['dispatched', 'delivered'],
};

export function useOnlineOrdersScreen() {
  const route = useRoute();
  const branchId = useSessionStore((s) => s.currentBranchId);
  const [openOrders, setOpenOrders] = useState<CustomerOrderRow[]>([]);
  const [filter, setFilter] = useState<OrderFilterKey>('pending');
  const [orgId, setOrgId] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const skipBranchReload = useRef(true);

  const loadFn = useCallback(
    async (opts?: { force?: boolean }): Promise<ScreenRefreshResult | void> => {
      try {
        const oid = await getSessionOrganizationId();
        if (!oid || !branchId) {
          setOpenOrders([]);
          setOrgId(oid);
          return;
        }
        setOrgId(oid);
        const rows = await fetchOpenOrders(oid, branchId, undefined, {
          force: opts?.force,
        });
        setOpenOrders(rows);
      } finally {
        setInitialLoading(false);
      }
    },
    [branchId],
  );

  const { refreshing, onRefresh } = useScreenLoad(route.name, loadFn, { feedback: false });

  useEffect(() => {
    if (skipBranchReload.current) {
      skipBranchReload.current = false;
      return;
    }
    void onRefresh();
  }, [branchId, onRefresh]);

  const filterCounts = useMemo(() => {
    const counts: Record<OrderFilterKey, number> = {
      pending: 0,
      active: 0,
      ready: 0,
      dispatched: 0,
    };
    for (const order of openOrders) {
      for (const key of ORDER_FILTER_KEYS) {
        if (FILTER_STATUSES[key].includes(order.status)) {
          counts[key] += 1;
          break;
        }
      }
    }
    return counts;
  }, [openOrders]);

  const orders = useMemo(() => {
    const statuses = FILTER_STATUSES[filter];
    return openOrders.filter((o) => statuses.includes(o.status));
  }, [filter, openOrders]);

  // OBS #2 fix: only auto-redirect to Pendientes on the first load (when
  // initialLoading transitions to false). Avoids hijacking the active filter
  // on realtime updates while the merchant is looking at another segment.
  const userTouchedFilter = useRef(false);
  const handleSetFilter = useCallback((f: typeof filter) => {
    userTouchedFilter.current = true;
    setFilter(f);
  }, []);

  useEffect(() => {
    if (userTouchedFilter.current) return;
    if (!initialLoading) return;
    if (filterCounts.pending <= 0) return;
    if (filter === 'pending') return;
    if (filterCounts[filter] > 0) return;
    setFilter('pending');
  }, [filter, filterCounts, initialLoading]);

  return {
    orders,
    refreshing,
    initialLoading,
    onRefresh,
    filter,
    setFilter: handleSetFilter,
    filterCounts,
    orgId,
    branchId,
    pendingCount: filterCounts.pending,
  };
}
